/**
 * Glossary Data - Emotional Vocabulary & Nature Words
 * 
 * Extracted from all 6 chapters of "Where the Brave Things Grow"
 * Organized alphabetically with child-friendly definitions
 * 
 * Categories:
 * - Emotional Vocabulary: Words describing feelings and emotional states
 * - Nature Words: Forest creatures, plants, and natural elements
 */

export interface GlossaryEntry {
  word: string;
  definition: string;
  category: 'emotion' | 'nature';
  example?: string; // Optional: sentence from the book
}

export const glossaryData: GlossaryEntry[] = [
  // EMOTIONAL VOCABULARY
  {
    word: "brave",
    definition: "Having courage to try something even when you feel scared or unsure.",
    category: "emotion",
    example: "Pip was brave when he crossed the wobbly bridge."
  },
  {
    word: "calm",
    definition: "Feeling peaceful and relaxed, like a still pond with no ripples.",
    category: "emotion",
    example: "After taking pond breaths, Mila felt calm and ready."
  },
  {
    word: "confused",
    definition: "Not understanding what's happening or what to do next.",
    category: "emotion",
    example: "Mila felt confused when everyone scattered on the path."
  },
  {
    word: "connected",
    definition: "Feeling close to others, like you're part of a team or family.",
    category: "emotion",
    example: "The friends felt connected after sharing their gratitude leaves."
  },
  {
    word: "embarrassed",
    definition: "Feeling uncomfortable when something goes wrong in front of others.",
    category: "emotion",
    example: "Pip felt embarrassed when he tripped over the root."
  },
  {
    word: "excited",
    definition: "Feeling very happy and eager about something that's going to happen.",
    category: "emotion",
    example: "Mila was excited to find every secret spot in the forest."
  },
  {
    word: "frazzled",
    definition: "Feeling messy, overwhelmed, or scattered, like everything is happening too fast.",
    category: "emotion",
    example: "Mila's fur was all frazzled after the friends got mixed up."
  },
  {
    word: "gentle",
    definition: "Being kind, soft, and careful with others and their feelings.",
    category: "emotion",
    example: "Bella spoke in a gentle voice to help Pip feel better."
  },
  {
    word: "grateful",
    definition: "Feeling thankful and appreciating the good things you have.",
    category: "emotion",
    example: "Mila felt grateful for her warm nest and her friends."
  },
  {
    word: "grumpy",
    definition: "Feeling annoyed or cranky, like nothing is going the way you want.",
    category: "emotion",
    example: "The friends were all mixed up and grumpy on the path."
  },
  {
    word: "hurt",
    definition: "Feeling pain in your body or in your heart when something bad happens.",
    category: "emotion",
    example: "Pip felt hurt and embarrassed after he tripped."
  },
  {
    word: "kind",
    definition: "Being caring and helpful to others because you want them to feel good.",
    category: "emotion",
    example: "Mila showed kindness when she helped the lost hedgehog."
  },
  {
    word: "patient",
    definition: "Being able to wait calmly without getting upset or rushed.",
    category: "emotion",
    example: "Grandpa Tortoise said he felt patient and ready to help."
  },
  {
    word: "peaceful",
    definition: "Feeling very calm and quiet inside, like everything is just right.",
    category: "emotion",
    example: "Wise Frog sat peacefully on his lily pad."
  },
  {
    word: "proud",
    definition: "Feeling happy and good about something you or someone else did.",
    category: "emotion",
    example: "The friends felt proud of their brave steps across the bridge."
  },
  {
    word: "ready",
    definition: "Feeling prepared and eager to start something new.",
    category: "emotion",
    example: "After breathing, Mila felt ready to start the adventure."
  },
  {
    word: "relaxed",
    definition: "Feeling loose and comfortable, with no worries or tension.",
    category: "emotion",
    example: "Pip's face relaxed after he named his feelings."
  },
  {
    word: "sad",
    definition: "Feeling unhappy or down, like you might want to cry.",
    category: "emotion",
    example: "Bella felt sad for Pip when he got hurt."
  },
  {
    word: "scared",
    definition: "Feeling afraid or worried that something bad might happen.",
    category: "emotion",
    example: "Pip felt scared when he saw the high, wobbly bridge."
  },
  {
    word: "snapped",
    definition: "Speaking in a sharp, angry way when you're upset.",
    category: "emotion",
    example: "Pip snapped 'I'm fine!' even though he didn't look fine."
  },
  {
    word: "sniffled",
    definition: "Making little sniffing sounds when you're trying not to cry.",
    category: "emotion",
    example: "The hedgehog sniffled when Mila asked if he needed help."
  },
  {
    word: "trembling",
    definition: "Shaking slightly because you're scared, cold, or nervous.",
    category: "emotion",
    example: "Pip's whiskers were trembling when he looked at the bridge."
  },
  {
    word: "understood",
    definition: "Feeling like someone really knows how you feel and what you need.",
    category: "emotion",
    example: "Talking about needs made everyone feel understood."
  },
  {
    word: "worried",
    definition: "Feeling nervous or concerned that something might go wrong.",
    category: "emotion",
    example: "Mila felt worried for Pip when he pushed Bella away."
  },

  // NATURE WORDS
  {
    word: "acorn",
    definition: "A small nut that grows on oak trees, with a hard shell shaped like a tiny cup.",
    category: "nature",
    example: "The Squirrel Twins argued over the very last golden acorn."
  },
  {
    word: "bridge",
    definition: "A structure built to cross over water or a gap, like the wobbly wooden bridge.",
    category: "nature",
    example: "The bridge stretched over a sparkling stream."
  },
  {
    word: "bunny",
    definition: "A small, furry animal with long ears and a fluffy tail that loves to hop.",
    category: "nature",
    example: "Bella the bunny hopped to Pip's side."
  },
  {
    word: "butterflies",
    definition: "Beautiful insects with colorful wings that flutter from flower to flower.",
    category: "nature",
    example: "Butterflies filled the air in Kindness Meadow."
  },
  {
    word: "chameleon",
    definition: "A lizard that can change the color of its skin to match its surroundings or feelings.",
    category: "nature",
    example: "Camille the chameleon changed from green to blue to yellow."
  },
  {
    word: "chipmunks",
    definition: "Small, striped animals with chubby cheeks that store food.",
    category: "nature",
    example: "Chipmunks shared nut pieces with a hungry fawn."
  },
  {
    word: "deer",
    definition: "A graceful forest animal with long legs and sometimes antlers.",
    category: "nature",
    example: "Gentle Deer appeared with her eyes gentle and wise."
  },
  {
    word: "dew",
    definition: "Tiny drops of water that form on plants and grass in the early morning.",
    category: "nature",
    example: "Tessa wanted the shell to make a cup for gathering morning dew."
  },
  {
    word: "fawn",
    definition: "A baby deer with spotted fur.",
    category: "nature",
    example: "Chipmunks shared food with a hungry fawn."
  },
  {
    word: "festival",
    definition: "A special celebration with music, treats, and fun activities.",
    category: "nature",
    example: "The Forest Festival was just across the bridge."
  },
  {
    word: "forest",
    definition: "A large area filled with many trees, plants, and animals.",
    category: "nature",
    example: "Mila and her friends explored the secret spots in the forest."
  },
  {
    word: "frog",
    definition: "A small animal with smooth skin that hops and lives near water.",
    category: "nature",
    example: "Wise Frog sat peacefully on his lily pad."
  },
  {
    word: "garden",
    definition: "A special place where flowers, plants, or food grow with care.",
    category: "nature",
    example: "The Gratitude Garden burst with color."
  },
  {
    word: "hedgehog",
    definition: "A small animal covered in spiky quills that curl into a ball when scared.",
    category: "nature",
    example: "A young hedgehog sat alone, looking lost and sad."
  },
  {
    word: "lily pad",
    definition: "A flat, round leaf that floats on top of pond water.",
    category: "nature",
    example: "Wise Frog sat on a lily pad in the still pond."
  },
  {
    word: "meadow",
    definition: "A grassy field with wildflowers where animals play and rest.",
    category: "nature",
    example: "In Kindness Meadow, wildflowers swayed in the breeze."
  },
  {
    word: "mouse",
    definition: "A tiny, furry animal with a long tail, small ears, and a twitchy nose.",
    category: "nature",
    example: "Mila the mouse was wiggling with excitement."
  },
  {
    word: "oak tree",
    definition: "A tall, strong tree with wide branches and leaves, where acorns grow.",
    category: "nature",
    example: "The friends gathered at the big oak tree."
  },
  {
    word: "path",
    definition: "A trail or walkway that leads you from one place to another.",
    category: "nature",
    example: "The friends dashed down the path, leaves crunching under their feet."
  },
  {
    word: "petal",
    definition: "One of the soft, colorful parts of a flower.",
    category: "nature",
    example: "Gentle Deer handed Mila a petal with a message."
  },
  {
    word: "pond",
    definition: "A small body of still water where frogs, fish, and insects live.",
    category: "nature",
    example: "They reached a quiet, sparkling pond."
  },
  {
    word: "porcupine",
    definition: "An animal covered in sharp quills (spines) for protection.",
    category: "nature",
    example: "Pip the porcupine bounced on his toes with excitement."
  },
  {
    word: "ripples",
    definition: "Small waves that spread out on water when something touches it.",
    category: "nature",
    example: "Breathe out gently, like making pond ripples."
  },
  {
    word: "seedlings",
    definition: "Baby plants that are just starting to grow.",
    category: "nature",
    example: "Birds filled shell-cups with water for seedlings."
  },
  {
    word: "shell",
    definition: "The hard, protective outer covering of a nut, egg, or small creature.",
    category: "nature",
    example: "Tessa polished the sturdy shell into a perfect little cup."
  },
  {
    word: "sparkling",
    definition: "Shining brightly with little flashes of light, like stars or glitter.",
    category: "nature",
    example: "They reached a quiet, sparkling pond."
  },
  {
    word: "squirrel",
    definition: "A bushy-tailed animal that climbs trees and stores nuts for winter.",
    category: "nature",
    example: "The Squirrel Twins argued over the golden acorn."
  },
  {
    word: "stream",
    definition: "A small river of flowing water.",
    category: "nature",
    example: "The bridge stretched over a sparkling stream."
  },
  {
    word: "sunrise",
    definition: "The time in the morning when the sun comes up and the sky turns bright.",
    category: "nature",
    example: "The sun was shining bright as Mila and her friends followed the map."
  },
  {
    word: "sunset",
    definition: "The time in the evening when the sun goes down and the sky turns orange and pink.",
    category: "nature",
    example: "As sunset painted the forest orange and gold."
  },
  {
    word: "tortoise",
    definition: "A land turtle with a hard, domed shell that moves slowly and steadily.",
    category: "nature",
    example: "Grandpa Tortoise moved at his own slow and steady pace."
  },
  {
    word: "wildflowers",
    definition: "Colorful flowers that grow naturally without being planted.",
    category: "nature",
    example: "In the meadow, wildflowers swayed in the breeze."
  }
];

// Helper functions
export function getGlossaryByCategory(category: 'emotion' | 'nature') {
  return glossaryData.filter(entry => entry.category === category);
}

export function searchGlossary(searchTerm: string) {
  const lowerSearch = searchTerm.toLowerCase();
  return glossaryData.filter(entry => 
    entry.word.toLowerCase().includes(lowerSearch) ||
    entry.definition.toLowerCase().includes(lowerSearch)
  );
}

export function getAlphabeticalGroups() {
  const groups: Record<string, GlossaryEntry[]> = {};
  
  glossaryData.forEach(entry => {
    const firstLetter = entry.word[0].toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(entry);
  });
  
  return groups;
}
